import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SourceOutlined from "@mui/icons-material/SourceOutlined";
import {
  Box,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Company } from "../types";
import { hostname, parseError } from "../utils";

export default ({
  open,
  getToken,
  company,
  onClick,
  onClose,
  eventListener,
  title = "Select Folder",
  operationLoading = false,
}: {
  open: boolean;
  getToken: Function;
  company: Company;
  onClick: (folder: any) => void;
  onClose: () => void;
  eventListener: Function;
  title?: string;
  operationLoading?: boolean;
}) => {
  const [init, setInit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState<Array<any>>([]);
  const [openedFolders, setOpenedFolders] = useState<any>({});

  const getFolders = async () => {
    const currentToken = await getToken();
    axios
      .get(`${hostname}/documents/folders`, {
        params: {
          companyId: company.id,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setFolders(res.data.folders);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setInit(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (company.id) {
      getFolders();
    }
  }, [open, company]);

  // Helper to get color based on shareVisibility
  const getFolderColor = (folder: any): "primary" | "warning" | "inherit" => {
    if (folder.shareVisibility === "public") {
      return "warning";
    }
    if (folder.shareVisibility === "direct" || folder.shareVisibility === "company") {
      return "primary";
    }
    if (folder.name === "shared" && (folder.path === "/" || folder.path === "")) {
      return "primary";
    }
    return "inherit";
  };

  const getMenuItem = (folder: any, level: number = 0) => {
    var folderName = folder.name;
    const folderColor = getFolderColor(folder);
    if (
      folder.name === "shared" &&
      (folder.path === "/" || folder.path === "")
    ) {
      folderName = company.name + " Shared";
    }
    if (!folder.folders) {
      return (
        <ListItemButton
          key={folder.id}
          onClick={() => !operationLoading && onClick(folder)}
          disabled={operationLoading}
          style={{
            marginLeft: level * 15,
            opacity: operationLoading ? 0.6 : 1,
            cursor: operationLoading ? "not-allowed" : "pointer",
          }}
        >
          <ListItemIcon>
            <SourceOutlined color={folderColor} />
          </ListItemIcon>
          <ListItemText primary={folderName} />
        </ListItemButton>
      );
    } else {
      return (
        <React.Fragment key={folder.id}>
          <ListItemButton
            onClick={() => !operationLoading && onClick(folder)}
            disabled={operationLoading}
            style={{
              marginLeft: level * 15,
              opacity: operationLoading ? 0.6 : 1,
              cursor: operationLoading ? "not-allowed" : "pointer",
            }}
          >
            <ListItemIcon>
              <SourceOutlined color={folderColor} />
            </ListItemIcon>
            <ListItemText primary={folderName} />
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                if (!operationLoading) {
                  setOpenedFolders({
                    ...openedFolders,
                    [folder.id]: !openedFolders[folder.id],
                  });
                }
              }}
              disabled={operationLoading}
            >
              {openedFolders[folder.id] ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </ListItemButton>
          <Collapse in={openedFolders[folder.id]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {folder.folders.map((subFolder: any) =>
                getMenuItem(subFolder, level + 1)
              )}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={operationLoading ? () => {} : onClose}
      maxWidth="md"
    >
      <DialogTitle>{title}</DialogTitle>
      {operationLoading && <LinearProgress />}
      <DialogContent sx={{ position: "relative" }}>
        {loading && <div>Loading...</div>}
        {!folders && init && <div>No Results</div>}
        <List>
          <ListItemButton
            onClick={() =>
              !operationLoading &&
              onClick({
                name: "",
                path: "/",
              })
            }
            disabled={operationLoading}
            style={{
              opacity: operationLoading ? 0.6 : 1,
              cursor: operationLoading ? "not-allowed" : "pointer",
            }}
          >
            <ListItemIcon>
              <SourceOutlined />
            </ListItemIcon>
            <ListItemText primary={"/"} />
          </ListItemButton>
          {(folders || []).map((folder: any) => getMenuItem(folder))}
        </List>

        {operationLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress />
              <Box sx={{ textAlign: "center" }}>Processing...</Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
